'use client';

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import VerifiedBadge from '@/components/VerifiedBadge';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchConversations,
  fetchMessages,
  initialsFor,
  markRead,
  memberLabel,
  messageStamp,
  messageTime,
  sendMessage,
  type ConversationSummary,
  type MessageRow,
} from '@/lib/messaging';

function MessagingCentre() {
  const { user, loading: authLoading } = useAuth();
  const supabase = useMemo(() => createClient(), []);
  const searchParams = useSearchParams();

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const requestedId = searchParams?.get('c') ?? null;

  const loadConversations = useCallback(
    async (preferId?: string | null) => {
      if (!user?.id) return;
      setError(null);
      try {
        const list = await fetchConversations(supabase, user.id);
        setConversations(list);
        setSelectedId((current) => {
          const wanted = preferId ?? current ?? requestedId;
          if (wanted && list.some((c) => c.id === wanted)) return wanted;
          return list[0]?.id ?? null;
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load your messages.');
      } finally {
        setLoading(false);
      }
    },
    [supabase, user?.id, requestedId]
  );

  useEffect(() => {
    if (!authLoading && user?.id) loadConversations();
    else if (!authLoading) setLoading(false);
  }, [authLoading, user?.id, loadConversations]);

  // Open a thread: load it, then mark the other side's messages read.
  useEffect(() => {
    if (!selectedId || !user?.id) {
      setMessages([]);
      return;
    }
    let active = true;
    setLoadingThread(true);
    (async () => {
      try {
        const rows = await fetchMessages(supabase, selectedId);
        if (!active) return;
        setMessages(rows);
        const hadUnread = rows.some((m) => m.sender_id !== user.id && !m.read_at);
        if (hadUnread) {
          await markRead(supabase, selectedId, user.id);
          setConversations((prev) =>
            prev.map((c) => (c.id === selectedId ? { ...c, unread: 0 } : c))
          );
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Could not open that conversation.');
      } finally {
        if (active) setLoadingThread(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedId, supabase, user?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  const selected = conversations.find((c) => c.id === selectedId) ?? null;

  const filtered = conversations.filter((c) => {
    if (!searchQuery.trim()) return true;
    const needle = searchQuery.toLowerCase();
    return (
      memberLabel(c.otherMember).toLowerCase().includes(needle) ||
      (c.subject ?? '').toLowerCase().includes(needle) ||
      (c.lastMessage?.body ?? '').toLowerCase().includes(needle)
    );
  });

  const totalUnread = conversations.reduce((sum, c) => sum + c.unread, 0);

  const handleSend = async () => {
    const body = newMessage.trim();
    if (!body || !selectedId || sending) return;
    setSending(true);
    setError(null);
    try {
      const saved = await sendMessage(supabase, selectedId, body);
      setMessages((prev) => [...prev, saved]);
      setNewMessage('');
      setConversations((prev) =>
        prev
          .map((c) =>
            c.id === selectedId
              ? { ...c, lastMessage: saved, last_message_at: saved.created_at }
              : c
          )
          .sort(
            (a, b) =>
              new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
          )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your message could not be sent.');
    } finally {
      setSending(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="bg-primary text-primary-foreground py-8">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
            <div className="h-8 w-64 bg-white/20 rounded animate-pulse" />
          </div>
        </div>
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6">
          <div className="card h-[60vh] animate-pulse bg-muted/40" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center card max-w-md mx-auto">
          <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Sign in to view messages</h2>
          <p className="text-muted-foreground text-sm mb-4">
            You need to be logged in to access your messaging centre.
          </p>
          <Link href="/sign-up-login-screen" className="btn-primary">Sign In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-primary text-primary-foreground py-8">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <Icon name="ChatBubbleLeftRightIcon" size={28} />
            <div>
              <h1 className="text-2xl font-extrabold">Messaging Centre</h1>
              <p className="text-green-200 text-sm mt-0.5">
                {totalUnread > 0
                  ? `${totalUnread} unread message${totalUnread > 1 ? 's' : ''}`
                  : conversations.length === 0
                    ? 'No conversations yet'
                    : 'All messages read'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6">
        {error && (
          <div
            className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-4 mb-5"
            role="alert"
          >
            <Icon name="ExclamationTriangleIcon" size={20} className="text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {conversations.length === 0 ? (
          <div className="card flex flex-col items-center justify-center py-20 text-center">
            <Icon name="ChatBubbleLeftRightIcon" size={44} className="text-muted-foreground mb-4" />
            <h2 className="text-lg font-bold text-foreground mb-1">No conversations yet</h2>
            <p className="text-muted-foreground text-sm max-w-md mb-6">
              When you enquire about a listing, or answer someone&apos;s wanted ad, the conversation
              appears here so you can carry on the discussion in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link href="/marketplace-listings" className="btn-primary">
                <Icon name="MagnifyingGlassIcon" size={18} />
                Browse listings
              </Link>
              <Link href="/buyer-requests" className="btn-secondary">
                <Icon name="ClipboardDocumentListIcon" size={18} />
                See what buyers want
              </Link>
            </div>
          </div>
        ) : (
          <div
            className="flex flex-col lg:flex-row gap-0 bg-card border border-border rounded-xl overflow-hidden shadow-sm"
            style={{ minHeight: '70vh' }}
          >
            {/* ── Thread list ── */}
            <aside className="w-full lg:w-80 xl:w-96 border-b lg:border-b-0 lg:border-r border-border flex flex-col">
              <div className="p-3 border-b border-border">
                <div className="relative">
                  <Icon
                    name="MagnifyingGlassIcon"
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search messages…"
                    className="form-input pl-9 text-sm"
                    aria-label="Search conversations"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto max-h-[30vh] lg:max-h-[62vh]">
                {filtered.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4">Nothing matches that search.</p>
                ) : (
                  filtered.map((c) => {
                    const name = memberLabel(c.otherMember);
                    const isActive = c.id === selectedId;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setSelectedId(c.id)}
                        className={`w-full text-left px-4 py-3 border-b border-border flex gap-3 transition-colors ${
                          isActive ? 'bg-primary/5' : 'hover:bg-muted/50'
                        }`}
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                          {initialsFor(name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-bold text-sm text-foreground truncate">{name}</span>
                            <span className="text-[11px] text-muted-foreground shrink-0">
                              {c.lastMessage ? messageTime(c.lastMessage.created_at) : ''}
                            </span>
                          </div>
                          {c.subject && (
                            <p className="text-[11px] text-primary truncate mt-0.5">{c.subject}</p>
                          )}
                          <div className="flex items-center justify-between gap-2 mt-0.5">
                            <p className="text-xs text-muted-foreground truncate">
                              {c.lastMessage?.body ?? 'No messages yet'}
                            </p>
                            {c.unread > 0 && (
                              <span className="shrink-0 bg-accent text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                                {c.unread}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </aside>

            {/* ── Thread ── */}
            <section className="flex-1 flex flex-col min-w-0">
              {selected ? (
                <>
                  <header className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="font-bold text-foreground truncate">
                          {memberLabel(selected.otherMember)}
                        </h2>
                        {selected.otherMember?.cph_verified && <VerifiedBadge type="standard" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {[selected.subject, selected.otherMember?.county]
                          .filter(Boolean)
                          .join(' · ') || 'Direct message'}
                      </p>
                    </div>
                    {selected.listing_id && (
                      <Link
                        href={`/listing/${selected.listing_id}`}
                        className="btn-secondary text-xs py-2 px-3 whitespace-nowrap shrink-0"
                      >
                        View listing
                      </Link>
                    )}
                    {!selected.listing_id && selected.buyer_request_id && (
                      <Link
                        href="/buyer-requests"
                        className="btn-secondary text-xs py-2 px-3 whitespace-nowrap shrink-0"
                      >
                        View wanted ads
                      </Link>
                    )}
                  </header>

                  <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 max-h-[50vh]">
                    {loadingThread ? (
                      <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div key={`ts-${i}`} className="h-14 bg-muted/50 rounded-xl animate-pulse" />
                        ))}
                      </div>
                    ) : messages.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No messages in this conversation yet — say hello.
                      </p>
                    ) : (
                      messages.map((m) => {
                        const isOwn = m.sender_id === user.id;
                        return (
                          <div
                            key={m.id}
                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[78%] ${isOwn ? 'items-end' : 'items-start'}`}>
                              <div
                                className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                                  isOwn
                                    ? 'bg-primary text-primary-foreground rounded-br-md'
                                    : 'bg-muted text-foreground rounded-bl-md'
                                }`}
                              >
                                {m.body}
                              </div>
                              <p
                                className={`text-[11px] text-muted-foreground mt-1 ${
                                  isOwn ? 'text-right' : 'text-left'
                                }`}
                              >
                                {messageStamp(m.created_at)}
                                {isOwn && m.read_at ? ' · Read' : ''}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={bottomRef} />
                  </div>

                  <div className="border-t border-border p-3 flex items-end gap-2">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      rows={2}
                      placeholder="Write a message… (Enter to send, Shift+Enter for a new line)"
                      className="form-input text-sm resize-none flex-1"
                    />
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={sending || !newMessage.trim()}
                      className="btn-primary px-4 py-3 disabled:opacity-50"
                      aria-label="Send message"
                    >
                      <Icon name="PaperAirplaneIcon" size={18} />
                      <span className="hidden sm:inline">{sending ? 'Sending…' : 'Send'}</span>
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                  Pick a conversation on the left.
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

// useSearchParams (?c=<thread>) needs a Suspense boundary.
export default function MessagingCentreScreen() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-background">
          <div className="bg-primary py-8">
            <div className="max-w-screen-2xl mx-auto px-4 lg:px-8">
              <div className="h-8 w-64 bg-white/20 rounded animate-pulse" />
            </div>
          </div>
        </div>
      }
    >
      <MessagingCentre />
    </Suspense>
  );
}
